import React, { useEffect, useMemo, useState } from "react";

import "./DashboardSummary.css";

import { useNavigate } from "react-router-dom";

import { useCart } from "./CartContext";

import { useUser } from "./UserContext";

const API_BASE_URL = "https://connnet4you-server.onrender.com";

const IMAGE_BASE_URL = "https://www.jusping.com/images/shops";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.connectfree4u.connectfree4u";

// Keep your original shop slug mappings
const shopIcons = {
  "Kanji-Sweets": "🧁",
  ALNazeerMuradabadiChickenBiryani: "🍗",
  Janta7DaysChineseFastFood: "🥡",
  QureshiKababCenter: "🍢",
  "Vow-vista": "🌅",
  RajeevVegStore: "🥬",
  "Ganga-Medical-hall": "💊",
  "Desi-swaad": "🍛",
  "Home-chef": "🥮",
  TheVegKingFastFood: "🥡",
  Zerocollection: "🌅",
  "DivineCafe&FastFood": "🥡",
  YadavTransport: "🚚",
  PatanjaliArogyaKendra: "🌿",
  SecondWifeFamilyRestaurant: "👩‍🍳",
  NaginderLittiHouse: "🍘",
  FarukShawarmaPoint: "🍛",
  AardeesChickenShicken: "🍗",
  OmSaiKitchen: "🍽️",
  BalajiGrill: "🔥",
  MohanLamaAmazingMomos: "🥟",
  RajaZaikaKalkattaKathiRoll: "🌯",
  ShadhuIcecream: "🧁",
  "City-Home-Services": "🏠",
  "test-flow-7": "🏠",
  "surya-prakash-tution-classes": "🏠",
};

const displayName = (slug = "") =>
  slug
    .replace(/-/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/&/g, " & ");

// Static service data for image and icon fallback
const staticServices = [
  {
    id: 1,
    name: "Home Cleaning",
    price: 299,
    category: "Home Services",
    image:
      "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=500&q=80",
    icon: "🏠",
  },
  {
    id: 2,
    name: "AC Repair & Service",
    price: 499,
    category: "AC Repair",
    image:
      "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&w=500&q=80",
    icon: "❄️",
  },
  {
    id: 3,
    name: "Plumber",
    price: 299,
    category: "Plumbing",
    image:
      "https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?auto=format&fit=crop&w=500&q=80",
    icon: "🔧",
  },
  {
    id: 4,
    name: "Electrician",
    price: 299,
    category: "Electrical",
    image:
      "https://images.unsplash.com/photo-1621905251918-48416bd8575a?auto=format&fit=crop&w=500&q=80",
    icon: "⚡",
  },
  {
    id: 5,
    name: "Beauty & Wellness",
    price: 499,
    category: "Beauty",
    image:
      "https://images.unsplash.com/photo-1560750588-73207b1ef5b8?auto=format&fit=crop&w=500&q=80",
    icon: "💗",
  },
  {
    id: 6,
    name: "Car/Bike Service",
    price: 499,
    category: "Vehicle Service",
    image:
      "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&w=500&q=80",
    icon: "🚘",
  },
];

// Match live API services with local images and icons
const getStaticServiceAsset = (service) => {
  const serviceName = (
    service?.title ||
    service?.name ||
    ""
  )
    .toLowerCase()
    .trim();

  const serviceCategory = (service?.category || "")
    .toLowerCase()
    .trim();

  return staticServices.find((item) => {
    const staticName = item.name.toLowerCase().trim();
    const staticCategory = item.category.toLowerCase().trim();

    return (
      (serviceName && staticName === serviceName) ||
      (serviceCategory && staticCategory === serviceCategory)
    );
  });
};

const getServiceImage = (service) =>
  getStaticServiceAsset(service)?.image ||
  service?.image_url ||
  "";

const getServiceIcon = (service) =>
  getStaticServiceAsset(service)?.icon || "🔧";

// Keep future categories commented
const categories = [
  {
    name: "Shops",
    icon: "🛒",
    color: "orange",
    path: "/shops",
  },
  {
    name: "Services",
    icon: "🔧",
    color: "blue",
    path: "/services",
  },
  // { name: "Chat", icon: "▣", color: "green", path: "/chat" },
  // { name: "Community", icon: "👥", color: "purple", path: "/community" },
  // { name: "Matrimony", icon: "❤️", color: "pink", path: "/matrimony" },
  // { name: "AstroTalk", icon: "✨", color: "yellow", path: "/astrotalk" },
  // { name: "Jobs", icon: "▣", color: "blue", path: "/jobs" },
  // { name: "Real Estate", icon: "⌂", color: "green", path: "/real-estate" },
  // { name: "Premium", icon: "♛", color: "yellow", path: "/premium" },
  // { name: "More", icon: "•••", color: "gray", path: "/more" },
];

const DashboardSummary = () => {
  const navigate = useNavigate();

  const [shops, setShops] = useState([]);
  const [search, setSearch] = useState("");
  const [visitCount, setVisitCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const [notificationCount] = useState(1);

  // My Business
  const [myBusiness, setMyBusiness] = useState(null);
  const [businessLoading, setBusinessLoading] = useState(true);

  // Live services state
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);

  const { clearAllCarts } = useCart();
  const { setUser } = useUser();

  // Keep context integrations available.
  // Do not clear carts or reset the user on dashboard render.

  const getServiceImage = (service) => {
    const title = (service.title || "").toLowerCase();
    const category = (service.category || "").toLowerCase();

    if (
      title.includes("ac") ||
      category.includes("ac repair")
    ) {
      return "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&w=500&q=80";
    }

    if (
      category.includes("plumbing") ||
      title.includes("plumbing")
    ) {
      return "https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?auto=format&fit=crop&w=500&q=80";
    }

    if (
      category.includes("electrical") ||
      title.includes("electrician")
    ) {
      return "https://images.unsplash.com/photo-1621905252507-b35492cc74b4?auto=format&fit=crop&w=500&q=80";
    }

    if (category.includes("cleaning")) {
      return "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=500&q=80";
    }

    if (
      category.includes("appliance") ||
      title.includes("washing machine")
    ) {
      return "https://images.unsplash.com/photo-1626806787461-102c1bfaaea1?auto=format&fit=crop&w=500&q=80";
    }

    return "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=500&q=80";
  };

  const getServiceIcon = (service) => {
    const category = (service.category || "").toLowerCase();

    if (category.includes("ac repair")) return "❄️";
    if (category.includes("plumbing")) return "🔧";
    if (category.includes("electrical")) return "⚡";
    if (category.includes("cleaning")) return "🧹";
    if (category.includes("appliance")) return "🛠️";

    return "🏠";
  };

  // -----------------------------
  // Visit analytics - existing API
  // -----------------------------
  useEffect(() => {
    const fetchVisitCount = async () => {
      try {
        await fetch(`${API_BASE_URL}/api/analytics/visit`, {
          method: "POST",
        });

        const response = await fetch(
          `${API_BASE_URL}/api/analytics/visit-count`
        );

        if (!response.ok) return;

        const data = await response.json();

        setVisitCount(
          data.count ??
            data.total ??
            data.visits ??
            null
        );
      } catch (error) {
        console.error(
          "Visit analytics error:",
          error
        );
      }
    };

    fetchVisitCount();
  }, []);

  // -----------------------------
  // Nearby + approved public shops
  // -----------------------------
  useEffect(() => {
    let cancelled = false;

    const getShopList = (data) => {
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.shops)) return data.shops;
      if (Array.isArray(data?.stores)) return data.stores;
      if (Array.isArray(data?.data)) return data.data;
      return [];
    };

    const getShopKey = (shop) =>
      String(
        shop?.slug ||
          shop?.shop_slug ||
          shop?.id ||
          shop?._id ||
          ""
      )
        .trim()
        .toLowerCase();

    const fetchPublicShops = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/shops/public`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch public shops");
        }

        return getShopList(await response.json());
      } catch (error) {
        console.error(
          "Failed to fetch public shops:",
          error
        );

        return [];
      }
    };

    const mergeAndSetShops = async (nearbyShops = []) => {
      const publicShops = await fetchPublicShops();

      if (cancelled) return;

      // Nearby results come first so their distance values are preserved.
      const merged = new Map();

      [...nearbyShops, ...publicShops].forEach((shop) => {
        if (!shop || typeof shop !== "object") return;

        const key = getShopKey(shop);

        if (!key) return;

        if (!merged.has(key)) {
          merged.set(key, shop);
        } else {
          // Keep the nearby record and fill any missing fields from public data.
          merged.set(key, {
            ...shop,
            ...merged.get(key),
          });
        }
      });

      setShops(Array.from(merged.values()));
      setLoading(false);
    };

    const fetchNearbyShops = async (latitude, longitude) => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/shops?lat=${latitude}&lng=${longitude}`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch nearby shops");
        }

        const nearbyShops = getShopList(
          await response.json()
        );

        await mergeAndSetShops(nearbyShops);
      } catch (error) {
        console.error(
          "Failed to fetch nearby shops:",
          error
        );

        await mergeAndSetShops([]);
      }
    };

    if (!navigator.geolocation) {
      setLocationError(true);
      mergeAndSetShops([]);

      return () => {
        cancelled = true;
      };
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        if (!cancelled) {
          fetchNearbyShops(
            coords.latitude,
            coords.longitude
          );
        }
      },
      (error) => {
        console.warn(
          "Location unavailable:",
          error.message
        );

        if (!cancelled) {
          setLocationError(true);
          mergeAndSetShops([]);
        }
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000,
      }
    );

    return () => {
      cancelled = true;
    };
  }, []);

  // -----------------------------
  // Fetch services from API
  // -----------------------------
  useEffect(() => {
    let cancelled = false;

    const fetchServices = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/services?shopId=25`
        );

        if (!response.ok) {
          throw new Error(
            "Failed to fetch services"
          );
        }

        const data = await response.json();

        const serviceList = Array.isArray(data)
          ? data
          : Array.isArray(data?.services)
          ? data.services
          : [];

        // Show only published services
        const publishedServices =
          serviceList.filter(
            (service) =>
              service.status === "published"
          );

        if (!cancelled) {
          setServices(publishedServices);
        }
      } catch (error) {
        console.error(
          "Failed to fetch services:",
          error
        );

        if (!cancelled) {
          setServices([]);
        }
      } finally {
        if (!cancelled) {
          setServicesLoading(false);
        }
      }
    };

    fetchServices();

    return () => {
      cancelled = true;
    };
  }, []);
    // -----------------------------
  // My Business status
  // -----------------------------
  useEffect(() => {
    let cancelled = false;

    const fetchMyBusiness = async () => {
      const token = localStorage.getItem("authToken");

      // User is not logged in
      if (!token) {
        if (!cancelled) {
          setMyBusiness(null);
          setBusinessLoading(false);
        }
        return;
      }

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/shops/vendor`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch business");
        }

        const data = await response.json();

        const currentShop = Array.isArray(data)
          ? data[0]
          : data;

        if (!cancelled) {
          setMyBusiness(currentShop || null);
        }
      } catch (error) {
        console.error(
          "[Dashboard] Failed to fetch business:",
          error
        );

        if (!cancelled) {
          setMyBusiness(null);
        }
      } finally {
        if (!cancelled) {
          setBusinessLoading(false);
        }
      }
    };

    fetchMyBusiness();

    return () => {
      cancelled = true;
    };
  }, []);

  // -----------------------------
  // Existing shop navigation
  // -----------------------------
// -----------------------------
// Centralized service navigation
// -----------------------------
const navigateToServiceStore = (params = {}) => {
  const { shopSlug, shopId, serviceId } = params;

  // No particular store selected: open the central services directory.
  if (!shopSlug) {
    navigate("/services");
    return;
  }

  const url = new URL(
    `https://${shopSlug}.jusping.com/services`
  );

  if (shopId != null) {
    url.searchParams.set("shopId", String(shopId));
  }

  if (serviceId != null) {
    url.searchParams.set("serviceId", String(serviceId));
  }

  window.location.assign(url.toString());
};

// -----------------------------
// Shop navigation
// -----------------------------
const handleClick = (shop) => {
  if (!shop) return;

  const slug = shop.slug || shop.shop_slug;

  if (!slug) {
    console.error("Shop slug missing:", shop);
    return;
  }


const storeType = String(
  shop.store_type ?? shop.storeType ?? ""
)
  .trim()
  .toLowerCase();
const page =
  storeType === "service" || storeType === "services"
    ? "services"
    : "products";

window.location.assign(`https://${slug}.jusping.com/${page}`);
};

// -----------------------------
// Navigate to selected service
// -----------------------------
const handleBookService = (service) => {
  if (!service) return;

  const serviceId =
    service.id ??
    service.service_id ??
    service.serviceId;

  if (serviceId == null) {
    console.error("Service ID is missing:", service);
    return;
  }

  const shopId =
    service.shopId ??
    service.shop_id ??
    service.shop?.id ??
    service.shop?.shopId;

  // First check whether the service already contains its store slug.
  // Otherwise, find its store in the shops already loaded on the dashboard.
  const matchedShop = shops.find(
    (shop) =>
      String(shop.id ?? shop.shopId ?? shop.shop_id) ===
      String(shopId)
  );

  const shopSlug =
    service.shopSlug ||
    service.shop_slug ||
    service.shop?.slug ||
    matchedShop?.slug ||
    matchedShop?.shop_slug;

  if (!shopSlug) {
    console.error(
      "Cannot identify the service store. Missing shop slug:",
      { service, shopId }
    );
    return;
  }

  navigateToServiceStore({
    shopSlug,
    shopId,
    serviceId,
  });
};

  // -----------------------------
  // Shop image - dynamic + legacy fallback
  // -----------------------------
  const getShopImage = (shop) => {
    const shopName = shop.name || displayName(shop.slug);
    const shopSlug = shop.slug || "";

    // 1. New uploaded image / Cloudinary URL
    if (shop.image_url) {
      const imageUrl = shop.image_url.trim();

      // Full URL - Cloudinary or any external image
      if (
        imageUrl.startsWith("http://") ||
        imageUrl.startsWith("https://")
      ) {
        return (
          <img
            className="jp-shop-img"
            src={imageUrl}
            alt={shopName}
            loading="lazy"
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src =
                `${IMAGE_BASE_URL}/logo.png`;
            }}
          />
        );
      }

      // Old database path such as:
      // images/shops/Ganga-Medical-hall.png
      return (
        <img
          className="jp-shop-img"
          src={`https://www.jusping.com/${imageUrl.replace(/^\/+/, "")}`}
          alt={shopName}
          loading="lazy"
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src =
              `${IMAGE_BASE_URL}/logo.png`;
          }}
        />
      );
    }

    // 2. Legacy slug-based image fallback
    const extensions = ["jpeg", "jpg", "png", "JPG"];

    return (
      <img
        className="jp-shop-img"
        src={`${IMAGE_BASE_URL}/${shopSlug}.${extensions[0]}`}
        alt={shopName}
        loading="lazy"
        onError={(event) => {
          const img = event.currentTarget;
          const attempt = Number(img.dataset.attempt || 0);

          if (attempt < extensions.length - 1) {
            img.dataset.attempt = String(attempt + 1);
            img.src =
              `${IMAGE_BASE_URL}/${shopSlug}.${extensions[attempt + 1]}`;
          } else {
            img.onerror = null;
            img.src = `${IMAGE_BASE_URL}/logo.png`;
          }
        }}
      />
    );
  };

  // Only display shops returned by the live APIs.
  // shopIcons is used exclusively for optional card icons.
  const list = shops;

  // -----------------------------
  // Search shops
  // -----------------------------
  const filteredShops = useMemo(() => {
    const query = search.toLowerCase().trim();

    return list.filter((shop) => {
      const name = (
        shop.name ||
        displayName(shop.slug)
      ).toLowerCase();

      const address = (shop.address || "").toLowerCase();
      const category = (shop.category || "").toLowerCase();

      return (
        name.includes(query) ||
        address.includes(query) ||
        category.includes(query)
      );
    });
  }, [list, search]);

  const visibleShops = showAll
    ? filteredShops
    : filteredShops.slice(0, 6);

  // -----------------------------
  // Navigation helpers
  // -----------------------------
  const goTo = (path) => {
    navigate(path);
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setShowAll(false);
  };

  // -----------------------------
  // Business CTA
  // -----------------------------
  const getBusinessCTA = () => {
    // No store
    if (!myBusiness) {
      return {
        title: "Start Your Business on JusPing",
        description:
          "Create your own Product or Service store and reach customers in your local community.",
        button: "Create Your Store",
        action: () => {
          const token = localStorage.getItem("authToken");

          if (!token) {
            navigate(
              `/login?redirect=${encodeURIComponent("/create-store")}`
            );
            return;
          }

          navigate("/create-store");
        },
      };
    }

    const status = myBusiness.tenant_status;

    // Draft
    if (status === "draft") {
      return {
        title: "Complete Your Store",
        description:
          "Your store is saved as a draft. Add your products or services and submit your store for approval.",
        button: "Complete Your Store",
        action: () => navigate("/my-business"),
      };
    }

    // Pending
    if (status === "pending") {
      return {
        title: "Store Under Review",
        description:
          "Your store has been submitted for admin approval. We'll activate it once the review is complete.",
        button: "View Store Status",
        action: () => navigate("/my-business"),
      };
    }

    // Active
    if (status === "approved") {
      return {
        title: "My Business",
        description:
          "Manage your JusPing business, products, services and store.",
        button: "Manage My Business",
        action: () => navigate("/my-business"),
      };
    }

    // Rejected
    if (status === "rejected") {
      return {
        title: "Update Your Store",
        description:
          "Your store needs some changes before it can be approved.",
        button: "Update Store Information",
        action: () => navigate("/my-business"),
      };
    }

    // Fallback
    return {
      title: "Start Your Business on JusPing",
      description:
        "Create your own Product or Service store and reach customers in your local community.",
      button: "Create Your Store",
      action: () => navigate("/create-store"),
    };
  };

  const businessCTA = getBusinessCTA();

  // -----------------------------
  // Loading
  // -----------------------------
  if (loading) {
    return (
      <div className="jp-loading">
        <div className="jp-loader-content">
          <div className="jp-loader-orbit">
            <div className="jp-loading-logo">J</div>
            <span className="jp-loader-dot">📍</span>
          </div>
          <h2>JusPing</h2>
          <p>Connecting you with local businesses...</p>
        </div>
      </div>
    );
  }
    return (
    <div className="jusping-dashboard">
      {/* HEADER */}
      {/* <header className="jp-header">
        ...
      </header> */}

      <main className="jp-main">
        {/* HERO BANNER */}
        <section className="jp-hero">
          <div className="jp-hero-content">
            <h1>
              Everything
              <br />
              You Need, Nearby
            </h1>

            <p>
              Shops • Services • Community • Chat • More
            </p>

            <div className="jp-hero-links">
              <span>
                <b className="hero-icon orange">📍</b>
                Local Shops
              </span>

              <span>
                <b className="hero-icon teal">🔧</b>
                Home Services
              </span>

              <span>
                <b className="hero-icon blue">▣</b>
                Chat & Community
              </span>

              {/* <span>
                <b className="hero-icon purple">✥</b>
                Premium Services
              </span> */}
            </div>
          </div>

          <div className="jp-hero-phone">
            <div className="jp-phone">
              <div className="jp-phone-notch" />

              <div className="jp-phone-screen">
                <div className="jp-phone-logo">●</div>

                <h2>
                  Jus<span>Ping</span>
                </h2>

                <p>
                  Connect
                  <br />
                  Shop
                  <br />
                  <b>Chat</b>
                  <br />
                  Grow
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CATEGORY ICONS */}
        <section className="jp-categories">
          {categories.map((category) => (
            <button
  className="jp-category"
  key={category.name}
  onClick={() => {
    if (category.name === "Services") {
      navigateToServiceStore();
    } else {
      navigate(category.path);
    }
  }}
>
              <span
                className={`jp-category-icon ${category.color}`}
              >
                {category.icon}
              </span>

              <span className="jp-category-label">
                {category.name}
              </span>
            </button>
          ))}
        </section>

        {/* BUSINESS STATUS / CTA */}
        <section className="jp-business-banner">
          <div className="jp-business-icon">🚀</div>

          <div className="jp-business-content">
            <h2>
              {businessLoading
                ? "Your Business on JusPing"
                : businessCTA.title}
            </h2>

            <p>
              {businessLoading
                ? "Checking your business status..."
                : businessCTA.description}
            </p>
          </div>

          {!businessLoading && (
            <button
              className="jp-business-button"
              onClick={businessCTA.action}
            >
              {businessCTA.button}
              <span>→</span>
            </button>
          )}
        </section>

        {/* NEARBY SHOPS */}
        <section className="jp-section">
          <div className="jp-section-heading">
            <h2>
              {search ? "Search Results" : "Nearby Shops"}
            </h2>

            <button onClick={() => setShowAll(!showAll)}>
              {showAll ? "Show Less ↑" : "View All"}{" "}
              <span>→</span>
            </button>
          </div>

          {visibleShops.length > 0 ? (
            <div className="jp-card-grid">
              {visibleShops.map((shop) => (
                <article
                  className="jp-shop-card"
                  key={
                    shop.slug ||
                    shop.shop_slug ||
                    shop.id ||
                    shop._id
                  }
                  role="link"
                  tabIndex={0}
                  onClick={() => handleClick(shop)}
                  onKeyDown={(event) => {
  if (
    event.key === "Enter" ||
    event.key === " "
  ) {
    event.preventDefault();
    handleClick(shop);
  }
}}
                >
                  <div className="jp-shop-image">
                    {getShopImage(shop)}
                  </div>

                  <div className="jp-shop-info">
                    <h3>
                      {shop.name ||
                        displayName(shop.slug)}
                    </h3>

                    <p className="jp-distance">
                      <span>📍</span>{" "}
                      {shop.distance ||
                        shop.distance_km ||
                        "Nearby"}
                    </p>

                    <div className="jp-shop-category">
                      <span
                        style={{
                          background:
                            shop.slug === "City-Home-Services"
                              ? "#0871e7"
                              : "#159957",
                        }}
                      >
                        {shopIcons[shop.slug] || "🏪"}
                      </span>

                      {shop.category || "Local Store"}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="jp-empty">
              <div className="jp-empty-icon">⌕</div>

              <h3>No stores found</h3>

              <p>
                Try another shop name or search term.
              </p>

              <button onClick={() => setSearch("")}>
                Clear search
              </button>
            </div>
          )}

          {locationError && (
            <p className="jp-location-message">
              Location unavailable. Showing available JusPing
              stores.
            </p>
          )}
        </section>

        {/* POPULAR SERVICES */}
        <section className="jp-section">
          <div className="jp-section-heading">
            <h2>Popular Services</h2>

            <button onClick={() => navigateToServiceStore()}>
  View All <span>→</span>
</button>
          </div>

          {servicesLoading ? (
            <p>Loading services...</p>
          ) : services.length > 0 ? (
            <div className="jp-card-grid">
              {services.map((service, index) => {
                const serviceName =
                  service.title ||
                  service.name ||
                  "Service";

                const servicePrice =
                  service.price ??
                  service.base_price;

                const serviceId =
                  service.id ??
                  service.service_id ??
                  `${serviceName}-${index}`;

                return (
                  <article
                    className="jp-service-card"
                    key={serviceId}
                  >
                    <div className="jp-service-image">
                      <img
                        src={getServiceImage(service)}
                        alt={serviceName}
                        loading="lazy"
                        onError={(event) => {
                          const staticAsset =
                            getStaticServiceAsset(service);

                          if (staticAsset?.image) {
                            event.currentTarget.onerror = null;
                            event.currentTarget.src =
                              staticAsset.image;
                          }
                        }}
                      />

                      <span
                        className={`jp-service-badge badge-${
                          index % 6
                        }`}
                      >
                        {getServiceIcon(service)}
                      </span>
                    </div>

                    <div className="jp-service-info">
                      <h3>{serviceName}</h3>

                      <p>
                        From{" "}
                        <strong>
                          {servicePrice != null
                            ? `₹${servicePrice}`
                            : "Contact for price"}
                        </strong>
                      </p>

                      <button
                        className="jp-book-button"
                        onClick={() =>
                          handleBookService(service)
                        }
                      >
                        Book Now
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="jp-empty">
              <h3>No services available</h3>
              <p>Please check back later.</p>
            </div>
          )}
        </section>

        {/* COMMUNITY CHAT */}
        <section className="jp-section jp-community-section">
          <div className="jp-section-heading">
            <h2>Community Chat</h2>

            <button onClick={() => goTo("/community")}>
              View All <span>→</span>
            </button>
          </div>

          <div className="jp-community-grid">
            <article className="jp-community-card jp-shop-chat">
              <div className="jp-community-symbol">▣</div>

              <div className="jp-community-content">
                <h3>Shop Owner - Customer Chat</h3>

                <p>
                  Chat directly with your favourite shop owners
                  for orders, support and updates.
                </p>

                <button onClick={() => goTo("/chat")}>
                  Start Chatting <span>→</span>
                </button>
              </div>

              <div className="jp-community-art">🏪</div>
            </article>

            <article className="jp-community-card jp-group-chat">
              <div className="jp-community-symbol">👥</div>

              <div className="jp-community-content">
                <h3>Join Community Chat</h3>

                <p>
                  Connect with people in your area. Discuss,
                  share and stay updated.
                </p>

                <button onClick={() => goTo("/community")}>
                  Join Now <span>→</span>
                </button>
              </div>

              <div className="jp-community-art">🧑‍🤝‍🧑</div>
            </article>
          </div>
        </section>

        {/* PREMIUM SERVICES */}
        {/* <section className="jp-premium">
          ...
        </section> */}
      </main>
            {/* FOOTER */}
      <footer className="jp-footer">
        <div className="jp-footer-main">
          <div className="jp-footer-brand">
            <div className="jp-footer-logo">
              <div className="jp-logo-mark">
                <span>●</span>
              </div>

              <h2>
                Jus<span>Ping</span>
              </h2>
            </div>

            <p>
              Connect Local • Build Community • Grow Together
            </p>
          </div>

          <div className="jp-footer-column">
            <h4>Quick Links</h4>
            <a href="/shops">Shops</a>
            <a href="/services">Services</a>
            <a href="/community">Community</a>
          </div>

          <div className="jp-footer-column">
            <h4>Support</h4>
            <a href="/about">About Us</a>
            <a href="/contact">Contact Us</a>
            <a href="/privacy-policy">Privacy Policy</a>
            <a href="/terms">Terms & Conditions</a>
          </div>

          <div className="jp-footer-column">
            <h4>Follow Us</h4>

            <div className="jp-socials">
              <a href="#facebook">f</a>
              <a href="#instagram">◎</a>
              <a href="#x">X</a>
              <a href="#linkedin">in</a>
              <a href="#youtube">▶</a>
            </div>
          </div>

          <div className="jp-footer-column jp-app-column">
            <h4>Download Our App</h4>

            <a
              className="jp-google-play"
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>▶</span>

              <small>
                GET IT ON
                <strong>Google Play</strong>
              </small>
            </a>
          </div>
        </div>

        <div className="jp-footer-bottom">
          <span>
            © {new Date().getFullYear()} JusPing.
            All rights reserved.
          </span>

          <span>
            Made with <b>♥</b> for Local Communities
          </span>
        </div>
      </footer>
    </div>
  );
};

export default DashboardSummary;